import { type Environment, fetchQuery, graphql } from 'react-relay';
import {
	type UIAnalyticsEvent,
	fireOperationalAnalytics,
} from '@atlassian/jira-product-analytics-bridge';
import { isClientFetchError } from '@atlassian/jira-fetch/src/utils/is-error.tsx';
import { TEN_MINUTES_IN_MS } from '../../common/constants.tsx';
import {
	setCachedAppRecommendationState,
	getCachedAppRecommendationState,
} from '../local-storage/index.tsx';
import type { AppRecommendations, AppRecommendationsQueryResult } from './types.tsx';

/* eslint-disable @atlassian/relay/unused-fields */
// eslint-disable-next-line @atlassian/relay/must-use-inline
export const fetchAppRecommendationsQuery = graphql`
	query fetchAppRecommendationsV2Query($cloudId: ID!) {
		growthRecommendations {
			recommendations(
				context: { useCase: "jiraProjectMenuRecommendations", tenantId: $cloudId, product: "jira" }
				first: 2
			) {
				... on GrowthRecRecommendations {
					data {
						... on GrowthRecJiraTemplateRecommendation {
							entityId @required(action: THROW)
						}
					}
				}
			}
		}
	}
`;

export const fetchAppRecommendations = async (
	cloudId: string,
	environment: Environment,
	analyticsEvent: UIAnalyticsEvent,
): Promise<AppRecommendations> => {
	const appRecommendationState = getCachedAppRecommendationState();

	if (
		appRecommendationState !== null &&
		Date.now() - appRecommendationState.fetchRecommendationTimeStamp < TEN_MINUTES_IN_MS
	) {
		return appRecommendationState.appRecommendations ?? [];
	}

	// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
	const data = (await fetchQuery(environment, fetchAppRecommendationsQuery, {
		cloudId,
	})
		.toPromise()
		.catch((err) => {
			const expectedErrors = [
				401, // Unauthorised
				403, // Forbidden
				null, // Client error
				undefined, // Client error
			];
			if (expectedErrors.includes(err.statusCode) || isClientFetchError(err)) return {};
			throw Error(`App recommendation ${err.name} error: ${err.message}`, { cause: err });
		})) as AppRecommendationsQueryResult;

	const appRecommendations = data?.growthRecommendations?.recommendations?.data;

	if (!appRecommendations) {
		fireOperationalAnalytics(
			analyticsEvent,
			'jiraAtlassianNavigation.fetchAppRecommendations empty',
			{
				response: `${JSON.stringify(data)}`,
			},
		);
	}

	setCachedAppRecommendationState({
		appRecommendations,
		fetchRecommendationTimeStamp: Date.now(),
	});

	return appRecommendations ?? [];
};
